import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { fetchUpcomingEvents } from "@/lib/events.functions";
import { EventCard } from "@/components/upcoming-events-section";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/components/route-boundaries";
import { socialImageMeta } from "@/lib/seo";
import { useTranslation } from "@/i18n";

export const Route = createFileRoute("/events/")({
  head: () => ({
    meta: [
      { title: "Card Shows & Trading Card Events UK | BidNova Auctions" },
      {
        name: "description",
        content:
          "Find upcoming trading card shows, trade nights, tournaments and grading submission events across the UK. Curated by BidNova Auctions.",
      },
      { property: "og:title", content: "Card Shows & Trading Card Events UK | BidNova Auctions" },
      {
        property: "og:description",
        content: "Upcoming UK trading card shows, tournaments and community meetups.",
      },
      { property: "og:url", content: "https://bidnovaauctions.uk/events" },
      { property: "og:type", content: "website" },
      ...socialImageMeta(),
    ],
    links: [{ rel: "canonical", href: "https://bidnovaauctions.uk/events" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://bidnovaauctions.uk/" },
            { "@type": "ListItem", position: 2, name: "Events", item: "https://bidnovaauctions.uk/events" },
          ],
        }),
      },
    ],
  }),
  component: EventsPage,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: RouteNotFoundBoundary,
});

function EventsPage() {
  const { t } = useTranslation("community");
  const { data, isLoading } = useQuery({
    queryKey: ["upcoming-events", "all"],
    queryFn: () => fetchUpcomingEvents(48),
    staleTime: 60_000,
  });
  const items = data ?? [];
  return (
    <div className="mx-auto max-w-7xl px-6 sm:px-8 py-14">
      <header className="mb-10">
        <p className="text-[11px] uppercase tracking-[0.2em] text-primary">{t("events.publicIndex.eyebrow", "Community")}</p>
        <h1 className="mt-2 font-display text-4xl text-white">{t("events.publicIndex.title", "Upcoming Card Shows & Events")}</h1>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          {t("events.publicIndex.subtitle", "Trading card shows, tournaments, trade nights, grading submission events and collector meetups happening across the UK.")}
        </p>
      </header>
      {isLoading ? (
        <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-[320px] rounded-2xl border border-white/10 skeleton-shimmer" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-10 text-center text-muted-foreground">
          {t("events.publicIndex.empty", "No upcoming events yet — check back soon.")}
        </div>
      ) : (
        <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((ev) => <EventCard key={ev.id} ev={ev} />)}
        </div>
      )}
    </div>
  );
}
